/**
 * EquiLife — _LAB:LAB upstream setter (cart add time)
 * ---------------------------------------------------
 * Replaces the `setLabProperties` half of the deprecated Shopify Script.
 *
 * What it does:
 *   Every time something is added to the cart (theme button, quick-add, or a
 *   client-side app such as Loyalty Lion), this checks the cart and makes sure
 *   any line whose SKU starts with "LAB-" carries the hidden property _LAB:LAB.
 *
 * Why this approach:
 *   The Ajax /cart/add.js payload carries variant IDs, not SKUs, so we can't
 *   tell a LAB item from the request alone. Instead we read /cart.js (which
 *   DOES return `sku` per line) right after each cart mutation, then patch any
 *   LAB line that's missing the property via /cart/change.js. Existing
 *   properties are preserved.
 *
 * BOGOS gift lines are EXCLUDED:
 *   BOGOS-managed free-gift lines carry their own hidden properties
 *   (_bogos_trigger, _bogos_trigger_id, _bogos_trigger_type) and have a
 *   discount code bound to that exact line. Patching such a line via
 *   /cart/change.js changes the line `key` (identity = variant + properties +
 *   selling plan), which breaks BOGOS's binding and causes the attached
 *   discount code to be rejected. We therefore skip any line that carries a
 *   `_bogos*` property. Those lines are still identified for fulfillment by
 *   the durable "LAB-" SKU prefix; if a downstream system needs the _LAB
 *   property ON the line, configure BOGOS to stamp it at gift-creation time
 *   instead of patching it here.
 *
 * Coverage:
 *   Covers all CLIENT-SIDE adds (theme + apps that add via the storefront Ajax
 *   API, including Loyalty Lion when it adds in the browser). It does NOT cover
 *   server-side add paths (headless Storefront API, ReCharge subscription
 *   inserts, draft orders) — those never touch the browser. For fulfillment,
 *   the durable source of truth remains the SKU prefix "LAB-".
 *
 * Install: see INSTALL.md. Load this once, globally, in theme.liquid.
 */
(function () {
  'use strict';

  var LAB_PREFIX = 'LAB-';
  var PROP_KEY = '_LAB';
  var PROP_VALUE = 'LAB';

  // Property-key prefix that marks a BOGOS-owned gift line. Any line carrying a
  // property whose key starts with this is left untouched (see header note).
  // Verify against your store: add the BOGOS gift, then in the console run
  //   fetch('/cart.js').then(r=>r.json()).then(c=>console.log(c.items.map(i=>i.properties)))
  // and widen this if the marker differs.
  var BOGOS_PROP_PREFIX = '_bogos';

  // Match cart writes we should react to. We deliberately EXCLUDE /cart/change
  // so our own patch requests don't retrigger the reconcile loop.
  var CART_WRITE = /\/cart\/(add|update|clear)(\.js)?(\?|$)/i;
  var CART_CHANGE = /\/cart\/change(\.js)?(\?|$)/i;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var inFlight = false;   // re-entrancy guard
  var queued = false;     // coalesce overlapping triggers

  function startsWithLab(sku) {
    return typeof sku === 'string' && sku.toUpperCase().indexOf(LAB_PREFIX) === 0;
  }

  // True if the line is a BOGOS-managed gift (carries a `_bogos*` property).
  // We must NOT touch these — patching them breaks the bound discount code.
  function isBogosGift(item) {
    if (!item || !item.properties) return false;
    for (var k in item.properties) {
      if (Object.prototype.hasOwnProperty.call(item.properties, k) &&
          k.toLowerCase().indexOf(BOGOS_PROP_PREFIX) === 0) {
        return true;
      }
    }
    return false;
  }

  function getCart() {
    return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Patch one line's properties, preserving anything already there.
  function patchLine(item) {
     console.log('----[_LAB] patching1 for third party apps----', item.sku, 'props seen:', JSON.stringify(item.properties));
    var props = {};
    if (item.properties) {
      for (var k in item.properties) {
        if (Object.prototype.hasOwnProperty.call(item.properties, k)) {
          props[k] = item.properties[k];
        }
      }
    }
    props[PROP_KEY] = PROP_VALUE;

    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      // Use the line `key` (stable identifier) rather than line index.
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props })
    }).then(function (r) { return r.json(); });
  }

  // Core reconcile: ensure every LAB- line has _LAB:LAB.
  function reconcile() {
    if (inFlight) { queued = true; return Promise.resolve(); }
    inFlight = true;

    return getCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;

        var needsPatch = cart.items.filter(function (item) {
          var hasProp = item.properties && item.properties[PROP_KEY];
          console.log("item.properties[_LAB]:")
          console.log(item.properties[PROP_KEY]);
          console.log("hasProp:")
          console.log(hasProp);
          console.log("startsWithLab(item.sku)")
          console.log(startsWithLab(item.sku));
          console.log("isBogosGift(item)")
          console.log(isBogosGift(item));
          // Skip BOGOS gift lines: patching them changes the line key and
          // breaks the discount code BOGOS binds to that line.
          return startsWithLab(item.sku) && !hasProp && !isBogosGift(item);
        });

        if (needsPatch.length === 0) return null;

        // Patch sequentially — line keys can shift as properties change.
        return needsPatch.reduce(function (chain, item) {
          return chain.then(function () { return patchLine(item); });
        }, Promise.resolve());
      })
      .then(function (result) {
        if (result) {
          // Let theme cart UIs (drawer, count bubble) know to refresh.
          document.dispatchEvent(new CustomEvent('equilife:lab-props-updated'));
        }
        return result;
      })
      .catch(function (err) {
        // Fail quietly in the storefront; log for debugging.
        if (window.console) console.warn('[EquiLife _LAB net]', err);
      })
      .then(function () {
        inFlight = false;
        if (queued) { queued = false; return reconcile(); }
      });
  }

  // --- Trigger 1: fetch() (Dawn and most modern themes/apps) ---
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
      var promise = origFetch.apply(this, args);
      if (CART_WRITE.test(url) && !CART_CHANGE.test(url)) {
        promise.then(function (res) { if (res && res.ok) reconcile(); }).catch(function () {});
      }
      return promise;
    };
  }

  // --- Trigger 2: XMLHttpRequest (older themes / apps) ---
  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (typeof url === 'string' && CART_WRITE.test(url) && !CART_CHANGE.test(url)) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) reconcile();
          });
        }
      } catch (e) { /* no-op */ }
      return origOpen.apply(this, arguments);
    };
  }

  // --- Trigger 3: page load (catch items added on a previous page) ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  // Expose for manual use / debugging.
  window.EquiLifeLabNet = { reconcile: reconcile };
})();